import type { Book } from "@/data/books";
import type { ReaderSignals } from "@/lib/recommendations";
import type { DnaSlice } from "@/lib/bookDna";

const lower = (s: string) => s.toLowerCase().trim();

export interface MatchExplanation {
  reasons: string[];
}

/**
 * Build human-readable "why this matched you" bullets for a book.
 * Pure function — no side effects, no DB calls.
 */
export const explainMatch = (
  book: Book,
  signals: ReaderSignals,
  options: { dnaSlices?: DnaSlice[]; reactionTallies?: Record<string, number> } = {},
): MatchExplanation => {
  const reasons: string[] = [];

  // Trope overlap
  const favSet = new Set(signals.favoriteTropes.map(lower));
  const matchedTropes = (book.tropes ?? []).filter((t) => favSet.has(lower(t)));
  if (matchedTropes.length > 0) {
    reasons.push(`You love ${matchedTropes.slice(0, 2).join(" + ")}`);
  }

  // Subgenre overlap
  const sgSet = new Set(signals.favoriteSubgenres.map(lower));
  const matchedSg = (book.subgenre ?? []).filter((s) => sgSet.has(lower(s)));
  if (matchedSg.length > 0) {
    reasons.push(`Matches your love of ${matchedSg[0]}`);
  }

  // Emotional intensity preference
  if (signals.emotionalPreference && book.emotionalIntensity === signals.emotionalPreference) {
    if (book.emotionalIntensity === "devastating") {
      reasons.push("Fits your emotionally devastating reads");
    } else if (book.emotionalIntensity === "light") {
      reasons.push("Cozy vibe, just how you like it");
    } else {
      reasons.push("Emotionally intense, exactly your speed");
    }
  }

  // Spice match
  if (signals.spicePreference != null) {
    const diff = Math.abs(book.spiceLevel - signals.spicePreference);
    if (diff === 0) reasons.push("Spice level matches your preference");
    else if (diff === 1) reasons.push("Spice is right in your range");
  }

  // DNA bucket overlap
  const tropesLower = (book.tropes ?? []).map(lower).join(" | ");
  for (const slice of options.dnaSlices ?? []) {
    const label = slice.label.toLowerCase();
    const hit =
      (label.includes("possessive") && /possessive|alpha|dominant|obsess/.test(tropesLower)) ||
      (label.includes("slow burn") && /slow burn/.test(tropesLower)) ||
      (label.includes("devastation") && book.emotionalIntensity === "devastating") ||
      (label.includes("cozy") && book.emotionalIntensity === "light") ||
      (label.includes("dark") && /dark|mafia/.test(tropesLower)) ||
      (label.includes("forbidden") && /forbidden|enemies|rivals/.test(tropesLower)) ||
      (label.includes("healing") && /healing|hurt\/comfort|grovel/.test(tropesLower)) ||
      (label.includes("morally gray") && /morally gray|anti-hero|villain/.test(tropesLower));
    if (hit) {
      reasons.push(`Matches your "${slice.label}" Book DNA`);
      break;
    }
  }

  // Reaction reinforcement
  const tallies = options.reactionTallies ?? {};
  if ((tallies["SLOW BURN TORTURE"] ?? 0) >= 2 && /slow burn/.test(tropesLower)) {
    reasons.push("You keep reacting 'Slow Burn Torture' to books like this");
  } else if ((tallies["TOUCH-HER-AND-DIE"] ?? 0) >= 2 && /possessive|alpha|dominant/.test(tropesLower)) {
    reasons.push("You react 'Touch-Her-And-Die' to this trope a lot");
  } else if ((tallies["CRYING"] ?? 0) >= 2 && book.emotionalIntensity === "devastating") {
    reasons.push("You've cried over books like this before");
  }

  // Fallback: always have at least one reason if we computed nothing
  if (reasons.length === 0) {
    if (book.featured) reasons.push("Hand-picked by Romance Radar editors");
    else reasons.push("Trending with readers who share your vibe");
  }

  return { reasons: reasons.slice(0, 4) };
};
